import { requestStatus } from 'src/utils/formRequestDecisions';
/* eslint-disable no-nested-ternary */
import { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
    Box,
    Typography,
    Stack,
    Chip,
    IconButton,
    Collapse,
    alpha,
    useTheme,
    useMediaQuery,
    Tooltip,
} from '@mui/material';
import Iconify from '../../../components/Iconify';
import SequenceStepDetail from './SequenceStepDetail';

// ─── Date helpers ──────────────────────────────────────────────────────────────
const parseApiDate = (dateStr) => {
    if (!dateStr) return null;
    const cleaned = dateStr.replace(/\.\d+/, '');
    const parsed = moment.utc(cleaned, 'DD-MM-YYYY HH:mm:ssZ');
    if (parsed.isValid()) return parsed.local();
    const iso = moment(dateStr);
    return iso.isValid() ? iso : null;
};

const fromNow = (dateStr) => {
    const m = parseApiDate(dateStr);
    return m ? m.fromNow() : '';
};

// ─── Step state config ────────────────────────────────────────────────────────
const STEP_CONFIG = {
    SUBMITTED: {
        label:       'Completed',
        color:       '#00AB55',
        bgColor:     'rgba(0,171,85,0.12)',
        icon:        'eva:checkmark-fill',
        borderColor: '#00AB55',
        pulse:       false,
    },
    IN_PROGRESS: {
        label:       'In Progress',
        color:       '#078DEE',
        bgColor:     'rgba(7,141,238,0.12)',
        icon:        'eva:edit-2-fill',
        borderColor: '#078DEE',
        pulse:       true,
    },
    OPENED: {
        label:       'Opened',
        color:       '#078DEE',
        bgColor:     'rgba(7,141,238,0.08)',
        icon:        'eva:eye-fill',
        borderColor: '#078DEE',
        pulse:       true,
    },
    CURRENT: {
        label:       'Current',
        color:       '#00AB55',
        bgColor:     'transparent',
        icon:        'eva:radio-button-on-fill',
        borderColor: '#00AB55',
        pulse:       false,
    },
    PENDING: {
        label:       'Pending',
        color:       '#919EAB',
        bgColor:     'transparent',
        icon:        'eva:radio-button-off-fill',
        borderColor: '#919EAB',
        pulse:       false,
    },
    EXPIRED: {
        label:       'Expired',
        color:       '#FF4842',
        bgColor:     'rgba(255,72,66,0.08)',
        icon:        'eva:alert-circle-fill',
        borderColor: '#FF4842',
        pulse:       false,
    },
    SKIPPED: {
        label:       'Skipped',
        color:       '#C4CDD5',
        bgColor:     'transparent',
        icon:        'eva:minus-circle-outline',
        borderColor: '#C4CDD5',
        pulse:       false,
    },
    REVOKED: {
        label:       'Revoked',
        color:       '#919EAB',
        bgColor:     'transparent',
        icon:        'eva:close-circle-fill',
        borderColor: '#919EAB',
        pulse:       false,
    },
};

const getStepConfig = (status) => STEP_CONFIG[status] || STEP_CONFIG.PENDING;

// Determine effective visual state for a step
// "CURRENT" is a synthetic state: link sent, not yet opened (status=PENDING but it IS the active step)
function resolveStepState(step, isActiveStep) {
    const status = requestStatus(step);
    if (status === 'PENDING' && isActiveStep) return 'CURRENT';
    return status;
}

// ─── Single Step circle ───────────────────────────────────────────────────────
function StepCircle({ state, stepNumber, isExpanded, onClick, tooltipOverride }) {
    const cfg = getStepConfig(state);
    return (
        <Tooltip title={tooltipOverride || cfg.label} arrow placement="top">
            <Box
                onClick={onClick}
                sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    border: `2px solid ${cfg.borderColor}`,
                    bgcolor: cfg.bgColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    flexShrink: 0,
                    transition: 'box-shadow 0.2s',
                    position: 'relative',
                    '&:hover': { boxShadow: `0 0 0 4px ${alpha(cfg.color, 0.14)}` },
                    ...(isExpanded && { boxShadow: `0 0 0 4px ${alpha(cfg.color, 0.18)}` }),
                    ...(cfg.pulse && {
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            inset: -4,
                            borderRadius: '50%',
                            border: `2px solid ${alpha(cfg.color, 0.4)}`,
                            animation: 'pulseRing 1.8s ease-out infinite',
                        },
                        '@keyframes pulseRing': {
                            '0%': { transform: 'scale(1)', opacity: 0.6 },
                            '100%': { transform: 'scale(1.5)', opacity: 0 },
                        },
                    }),
                }}
            >
                {state === 'PENDING' || state === 'CURRENT' || state === 'SKIPPED' ? (
                    <Typography variant="caption" sx={{ fontWeight: 700, color: cfg.color, fontSize: '12px', lineHeight: 1 }}>
                        {stepNumber}
                    </Typography>
                ) : (
                    <Iconify icon={cfg.icon} width={16} sx={{ color: cfg.color }} />
                )}
            </Box>
        </Tooltip>
    );
}

StepCircle.propTypes = {
    state:           PropTypes.string.isRequired,
    stepNumber:      PropTypes.number.isRequired,
    isExpanded:      PropTypes.bool.isRequired,
    onClick:         PropTypes.func.isRequired,
    tooltipOverride: PropTypes.string,
};

StepCircle.defaultProps = {
    tooltipOverride: null,
};

// ─── Connector line ───────────────────────────────────────────────────────────
function Connector({ leftCompleted, rightCompleted, vertical }) {
    const isComplete = leftCompleted && rightCompleted;
    const color = isComplete ? '#00AB55' : 'rgba(145,158,171,0.32)';
    if (vertical) {
        return (
            <Box sx={{
                width: 2,
                height: 24,
                bgcolor: color,
                borderRadius: 1,
                mx: 'auto',
                my: 0.5,
                transition: 'background-color 0.3s',
            }} />
        );
    }
    return (
        <Box sx={{
            flex: 1,
            height: 2,
            bgcolor: color,
            borderRadius: 1,
            mx: 0.5,
            mt: '17px', // align with circle center
            transition: 'background-color 0.3s',
            alignSelf: 'flex-start',
        }} />
    );
}

Connector.propTypes = {
    leftCompleted:  PropTypes.bool.isRequired,
    rightCompleted: PropTypes.bool.isRequired,
    vertical:       PropTypes.bool,
};
Connector.defaultProps = { vertical: false };

// ─── Stepper card ─────────────────────────────────────────────────────────────
export default function SequenceStepper({
    sequence,
    onResend,
    onCopyLink,
    onViewSubmission,
    onRemind,
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [expandedSteps, setExpandedSteps] = useState(new Set());
    const [collapsed, setCollapsed] = useState(sequence.isCompleted || false);

    const { name, created_at: createdAt, steps } = sequence;

    // Determine which step is "active" (first non-submitted, non-revoked, non-expired step)
    const activeStepIndex = steps.findIndex(
        (s) => s.status !== 'SUBMITTED' && s.status !== 'REVOKED' && s.status !== 'SKIPPED'
    );

    const toggleStep = (idx) => {
        setExpandedSteps((prev) => {
            const next = new Set(prev);
            if (next.has(idx)) next.delete(idx);
            else next.add(idx);
            return next;
        });
    };

    // Completed steps go straight to the submission dialog so users don't need a two-click discovery pattern.
    const handleStepClick = (step, idx) => {
        if (step?.status === 'SUBMITTED' && onViewSubmission) {
            onViewSubmission(step);
            return;
        }
        toggleStep(idx);
    };

    const isAllCompleted = steps.every((s) => s.status === 'SUBMITTED' || s.status === 'SKIPPED' || s.status === 'REVOKED');

    return (
        <Box
            sx={{
                borderRadius: '14px',
                border: '1px solid',
                borderColor: isAllCompleted ? 'rgba(0,171,85,0.2)' : 'rgba(145,158,171,0.2)',
                bgcolor: isAllCompleted ? 'rgba(0,171,85,0.02)' : 'background.paper',
                p: 2.5,
                transition: 'box-shadow 0.2s',
                '&:hover': { boxShadow: '0 2px 12px rgba(0,0,0,0.06)' },
            }}
        >
            {/* ── Header ── */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: collapsed ? 0 : 2.5 }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{
                        width: 32, height: 32, borderRadius: '8px', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: isAllCompleted ? 'rgba(0,171,85,0.1)' : 'rgba(133,92,248,0.1)',
                    }}>
                        <Iconify
                            icon={isAllCompleted ? 'eva:checkmark-circle-2-fill' : 'solar:list-bold-duotone'}
                            width={18}
                            sx={{ color: isAllCompleted ? '#00AB55' : '#855CF8' }}
                        />
                    </Box>
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '13px', lineHeight: 1.3 }}>
                            {name || 'Form Sequence'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                            {steps.length} forms · Sent {fromNow(createdAt)}
                        </Typography>
                    </Box>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    {isAllCompleted && (
                        <Chip
                            label="Complete"
                            size="small"
                            icon={<Iconify icon="eva:checkmark-fill" width={11} />}
                            sx={{ height: 20, fontSize: '10px', fontWeight: 700, bgcolor: 'rgba(0,171,85,0.1)', color: '#00AB55' }}
                        />
                    )}
                    <Tooltip title={collapsed ? 'Expand' : 'Collapse'}>
                        <IconButton size="small" onClick={() => setCollapsed((v) => !v)} sx={{ color: 'text.secondary' }}>
                            <Iconify icon={collapsed ? 'eva:chevron-down-fill' : 'eva:chevron-up-fill'} width={16} />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>

            {/* ── Steps ── */}
            <Collapse in={!collapsed}>
                {isMobile ? (
                    /* ─── Vertical layout for mobile ─── */
                    <Stack spacing={0}>
                        {steps.map((step, idx) => {
                            const state = resolveStepState(step, idx === activeStepIndex);
                            const cfg = getStepConfig(state);
                            const isExpanded = expandedSteps.has(idx);
                            const prevStep = idx > 0 ? steps[idx - 1] : null;
                            const prevName = prevStep?.form?.name || null;
                            const augmentedStep = { ...step, _prevFormName: prevName };

                            return (
                                <Box key={step.id || idx}>
                                    {idx > 0 && (
                                        <Connector
                                            leftCompleted={steps[idx - 1].status === 'SUBMITTED'}
                                            rightCompleted={step.status === 'SUBMITTED'}
                                            vertical
                                        />
                                    )}
                                    <Stack
                                        direction="row"
                                        spacing={1.5}
                                        alignItems="flex-start"
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => handleStepClick(step, idx)}
                                    >
                                        <StepCircle
                                            state={state}
                                            stepNumber={idx + 1}
                                            isExpanded={isExpanded}
                                            onClick={() => handleStepClick(step, idx)}
                                            tooltipOverride={step.status === 'SUBMITTED' && onViewSubmission ? 'View submission' : null}
                                        />
                                        <Box sx={{ flex: 1, pt: 0.5 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px', color: cfg.color === '#919EAB' ? 'text.secondary' : 'text.primary' }}>
                                                {step.form?.name || `Form ${idx + 1}`}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: cfg.color, fontWeight: 600, fontSize: '11px' }}>
                                                {cfg.label}
                                            </Typography>
                                            <Collapse in={isExpanded}>
                                                <SequenceStepDetail
                                                    step={augmentedStep}
                                                    onResend={onResend}
                                                    onCopyLink={onCopyLink}
                                                    onViewSubmission={onViewSubmission}
                                                    onRemind={onRemind}
                                                />
                                            </Collapse>
                                        </Box>
                                    </Stack>
                                </Box>
                            );
                        })}
                    </Stack>
                ) : (
                    /* ─── Horizontal layout for desktop ─── */
                    <Box>
                        {/* Step circles row */}
                        <Stack direction="row" alignItems="flex-start">
                            {steps.map((step, idx) => {
                                const state = resolveStepState(step, idx === activeStepIndex);
                                const isExpanded = expandedSteps.has(idx);
                                return (
                                    <Stack key={step.id || idx} direction="row" alignItems="flex-start" sx={{ flex: idx < steps.length - 1 ? 1 : 'none' }}>
                                        <Stack alignItems="center" sx={{ minWidth: 36 }}>
                                            <StepCircle
                                                state={state}
                                                stepNumber={idx + 1}
                                                isExpanded={isExpanded}
                                                onClick={() => handleStepClick(step, idx)}
                                                tooltipOverride={step.status === 'SUBMITTED' && onViewSubmission ? 'View submission' : null}
                                            />
                                        </Stack>
                                        {idx < steps.length - 1 && (
                                            <Connector
                                                leftCompleted={step.status === 'SUBMITTED'}
                                                rightCompleted={steps[idx + 1]?.status === 'SUBMITTED'}
                                            />
                                        )}
                                    </Stack>
                                );
                            })}
                        </Stack>

                        {/* Step labels row */}
                        <Stack direction="row" sx={{ mt: 0.75 }}>
                            {steps.map((step, idx) => {
                                const state = resolveStepState(step, idx === activeStepIndex);
                                const cfg = getStepConfig(state);
                                const isExpanded = expandedSteps.has(idx);
                                const isLast = idx === steps.length - 1;
                                const prevStep = idx > 0 ? steps[idx - 1] : null;
                                const prevName = prevStep?.form?.name || null;
                                const augmentedStep = { ...step, _prevFormName: prevName };

                                return (
                                    <Box
                                        key={step.id || idx}
                                        sx={{
                                            flex: isLast ? 'none' : 1,
                                            minWidth: 0,
                                            cursor: 'pointer',
                                            pr: isLast ? 0 : 1,
                                        }}
                                        onClick={() => handleStepClick(step, idx)}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: isExpanded ? 700 : 600,
                                                fontSize: '12px',
                                                color: cfg.color === '#919EAB' ? 'text.secondary' : 'text.primary',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {step.form?.name || `Form ${idx + 1}`}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: cfg.color, fontWeight: 600, fontSize: '10px', letterSpacing: '0.02em' }}>
                                            {cfg.label}
                                        </Typography>
                                        <Collapse in={isExpanded}>
                                            <SequenceStepDetail
                                                step={augmentedStep}
                                                onResend={onResend}
                                                onCopyLink={onCopyLink}
                                                onViewSubmission={onViewSubmission}
                                                onRemind={onRemind}
                                            />
                                        </Collapse>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Box>
                )}
            </Collapse>
        </Box>
    );
}

SequenceStepper.propTypes = {
    sequence: PropTypes.shape({
        id:          PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name:        PropTypes.string,
        created_at:  PropTypes.string,
        isCompleted: PropTypes.bool,
        steps:       PropTypes.arrayOf(PropTypes.object).isRequired,
    }).isRequired,
    onResend:         PropTypes.func,
    onCopyLink:       PropTypes.func,
    onViewSubmission: PropTypes.func,
    onRemind:         PropTypes.func,
};

SequenceStepper.defaultProps = {
    onResend:         null,
    onCopyLink:       null,
    onViewSubmission: null,
    onRemind:         null,
};
